import { groq } from 'next-sanity'
import { client } from '@/sanity/lib/client'
import { dataset, projectId } from '@/sanity/env'
import {
    TITLE_WITH_FALLBACKS,
    SUBTITLE_WITH_FALLBACKS,
    IMAGE_WITH_FALLBACKS,
} from '@/sanity/lib/groq-fragments'
import { SanityImage, Slug } from '@/sanity/types'
import { HeroGradientInfo } from '@/lib/recommendations'

/**
 * Search Engine
 *
 * Semantic search using native GROQ score(text::semanticSimilarity(...), boost(...)).
 * Falls back to GROQ text matching if the scored query fails.
 */

let hasLoggedEmbeddingsFailure = false

function formatEmbeddingsError(err: unknown): Record<string, unknown> {
    // @sanity/client errors commonly include statusCode and responseBody
    const e = err as { statusCode?: number; responseBody?: string; message?: string }
    return {
        statusCode: e?.statusCode,
        responseBody: typeof e?.responseBody === 'string'
            ? e.responseBody.slice(0, 500)
            : undefined,
        message: e?.message || String(err),
    }
}

export const SEARCHABLE_TYPES = [
    'article',
    'whitepaper',
    'caseStudy',
    'service',
    'industry',
    'partner',
    'page',
    'landing',
    'solution',
] as const

export type SearchableType = typeof SEARCHABLE_TYPES[number]

export type SearchResult = {
    _id: string
    _type: string
    _score?: number
    _createdAt?: string
    slug?: Slug | null
    title?: string
    image?: SanityImage | null
    subtitle?: string
    category?: string
    heroGradient?: HeroGradientInfo | null
}

export type SearchOptions = {
    /** Search query string */
    query: string
    /** Content types to search (defaults to all) */
    types?: SearchableType[]
    /** Number of results per page */
    limit?: number
    /** Offset for pagination */
    offset?: number
}

export type SearchResponse = {
    results: SearchResult[]
    total: number
    query: string
}

const SEARCH_TYPE_BOOSTS: Array<[string, number]> = [
    ['partner', 0.5],
    ['service', 0.4],
    ['industry', 0.4],
    ['solution', 0.3],
    ['page', 0.2],
    ['landing', 0.2],
    ['caseStudy', 0.1],
] // confirmed in Task 1; article/whitepaper unboosted

const SEARCH_PROJECTION = groq`{
    _id, _type, _createdAt,
    "slug": slug,
    "title": ${TITLE_WITH_FALLBACKS},
    "image": ${IMAGE_WITH_FALLBACKS},
    "subtitle": ${SUBTITLE_WITH_FALLBACKS},
    "category": category,
    "heroGradient": select(
        hero.variant == "heroLogoGradient" => {
            "variant": hero.variant,
            "gradientColors": hero.gradientColors,
            "heroLogo": hero.heroLogo
        },
        null
    )
}`

// Slice bounds are interpolated as validated integers (params are not supported in slices).
export function buildSearchQuery(offset: number, limit: number): string {
    const start = Math.max(0, Math.floor(offset))
    const end = start + Math.max(1, Math.floor(limit))
    const boosts = SEARCH_TYPE_BOOSTS
        .map(([type, value]) => `boost(_type == "${type}", ${value})`)
        .join(',\n            ')
    return groq`*[_type in $types && defined(slug.current)]
        | score(
            text::semanticSimilarity($q),
            ${boosts}
        )
        | order(_score desc)[${start}...${end}] ${SEARCH_PROJECTION}`
}

/**
 * Search content using native GROQ score/boost (semantic search)
 */
export async function searchContent(options: SearchOptions): Promise<SearchResponse> {
    const {
        query,
        types = [...SEARCHABLE_TYPES],
        limit = 10,
        offset = 0,
    } = options

    if (!query.trim()) {
        return { results: [], total: 0, query }
    }

    const validTypes = types.filter((t): t is SearchableType =>
        SEARCHABLE_TYPES.includes(t as SearchableType)
    )

    if (validTypes.length === 0) {
        return { results: [], total: 0, query }
    }

    try {
        const [results, totalCount] = await Promise.all([
            client.fetch<SearchResult[]>(buildSearchQuery(offset, limit), {
                types: validTypes,
                q: query.trim(),
            }),
            client.fetch<number>(
                `count(*[_type in $types && defined(slug.current)])`,
                { types: validTypes },
            ),
        ])

        return {
            results: results ?? [],
            total: Math.min(totalCount ?? 0, 100), // preserve prior capped-total UX
            query,
        }
    } catch (error) {
        if (!hasLoggedEmbeddingsFailure) {
            hasLoggedEmbeddingsFailure = true
            console.error('[Sanity embeddings] Semantic search query failed.', {
                projectId,
                dataset,
                ...formatEmbeddingsError(error),
            })
        }
        return fallbackGroqSearch(options)
    }
}

/**
 * Fallback GROQ-based search if semantic search fails
 */
async function fallbackGroqSearch(options: SearchOptions): Promise<SearchResponse> {
    const {
        query,
        types = [...SEARCHABLE_TYPES],
        limit = 10,
        offset = 0,
    } = options

    const searchPattern = `*${query.trim()}*`

    try {
        const response = await client.fetch<{ results: SearchResult[], total: number }>(
            `{
                "results": *[
                    _type in $types &&
                    defined(slug.current) &&
                    (
                        title match $searchPattern ||
                        card.title match $searchPattern ||
                        card.subtitle match $searchPattern ||
                        hero.title match $searchPattern ||
                        hero.subtitle match $searchPattern ||
                        seo.metaDescription match $searchPattern
                    )
                ] | order(_createdAt desc) [$offset...$end] {
                    _id,
                    _type,
                    _createdAt,
                    "slug": slug,
                    "title": ${TITLE_WITH_FALLBACKS},
                    "image": ${IMAGE_WITH_FALLBACKS},
                    "subtitle": ${SUBTITLE_WITH_FALLBACKS},
                    "category": category,
                    "heroGradient": select(
                        hero.variant == "heroLogoGradient" => {
                            "variant": hero.variant,
                            "gradientColors": hero.gradientColors,
                            "heroLogo": hero.heroLogo
                        },
                        null
                    )
                },
                "total": count(*[
                    _type in $types &&
                    defined(slug.current) &&
                    (
                        title match $searchPattern ||
                        card.title match $searchPattern ||
                        card.subtitle match $searchPattern ||
                        hero.title match $searchPattern ||
                        hero.subtitle match $searchPattern ||
                        seo.metaDescription match $searchPattern
                    )
                ])
            }`,
            {
                types,
                searchPattern,
                offset,
                end: offset + limit,
            }
        )

        return {
            results: response.results,
            total: response.total,
            query,
        }
    } catch (error) {
        console.error('Fallback search error:', error)
        return { results: [], total: 0, query }
    }
}

/**
 * Get human-readable label for content type
 */
export function getTypeLabel(type: string): string {
    const labels: Record<string, string> = {
        article: 'Blog',
        whitepaper: 'White Paper',
        caseStudy: 'Case Study',
        service: 'Service',
        industry: 'Industry',
        partner: 'Partner',
        page: 'Page',
        landing: 'Landing Page',
        solution: 'Solution',
    }
    return labels[type] ?? type
}
